export interface ProjectMetric {
  value: string;
  label: string;
  shimmer?: boolean; // Always show shimmer animation (not just on hover)
}

export interface HighlightStat {
  number: number;
  label: string;
  prefix?: string;
  suffix?: string;
  decimals?: number;
}

// Aggregate career impact stats for homepage
export const highlightStats: HighlightStat[] = [
  { number: 1, label: "Flagship Launches", prefix: "Day " },
  { number: 4.8, label: "App Ratings", suffix: "★", decimals: 1 },
  { number: 3, label: "Generated", prefix: "$", suffix: "MM+" },
];

export interface ProjectResult {
  value: string;
  label: string;
}

export interface ProcessStepMedia {
  src?: string;
  youtubeId?: string; // YouTube video ID for embeds
  caption?: string;
  type?: "image" | "video" | "youtube"; // defaults to "image"
  controls?: boolean; // Show video controls in lightbox (for prototype walkthroughs)
  placeholder?: boolean; // Show gray placeholder box instead of media
  playbackRate?: number; // Video playback speed (default 1)
  description?: string; // Text that renders left of image in paired layout
  aspectRatio?: string; // Force aspect ratio (e.g., "3.65/1") with object-fit: cover
}

export interface ProcessStepStat {
  number: number;
  label: string;
  suffix?: string;
}

export interface ProcessImageGroup {
  label?: string; // Optional label above the grid
  images: ProcessStepMedia[];
}

export interface ProcessStep {
  title: string;
  content: string;
  contentAfter?: string; // Text that appears after the image(s)
  conclusion?: string; // Text that appears after all images (final wrap-up)
  image?: ProcessStepMedia;
  images?: ProcessStepMedia[];
  imagesBeforeContentAfter?: ProcessStepMedia[]; // Images between content and contentAfter
  imageGroups?: ProcessImageGroup[]; // Multiple image grids with optional labels
  stats?: ProcessStepStat[]; // Animated stat blocks (like homepage hero)
  layout?: "stacked" | "side-by-side" | "side-by-side-left" | "three-col"; // side-by-side-left: image left, text right
  imageLayout?: "grid" | "single"; // single: force single column for images
  imagesBeforeLayout?: "grid" | "single"; // layout for imagesBeforeContentAfter (defaults to grid)
}

export interface ProcessApproach {
  title: string; // e.g., "Fan-Facing Design"
  points: string[]; // Bullet points
}

export interface UserQuote {
  text: string;
  author?: string; // Optional - can omit for anonymous research quotes
  context?: string; // e.g. "↳ (in reply)" - displayed before the quote
  url?: string; // Link to source
  publication?: string; // e.g. "Retail Brew, 2023" - displayed above the quote
}

export interface SolutionMedia {
  src: string;
  type: "image" | "video";
  caption?: string;
}

// A live, interactive prototype embedded in a case study as a first-class
// artifact (not a screenshot). Renders via LiveDemoStage: poster first, the
// iframe mounts on click so two full app routes don't boot with the modal.
// Gated demos rely on the case-study unlock minting their session cookies
// (see linkedExperiments + /api/projects/[slug]).
export interface LiveDemo {
  src: string; // Internal route, e.g. "/exp/n4"
  title: string; // Kicker above the stage, e.g. "Timed Text · In-Player"
  blurb?: string; // One-liner on the poster
  caption?: string; // Caption below the stage
  position?: "hero" | "secondAct"; // hero = the hero-media slot; secondAct = after Process
  aspect?: string; // CSS aspect-ratio, default "16/9"
  // External prototype that refuses framing (X-Frame-Options / CSP). The stage
  // keeps the poster and the launch button opens src in a new tab instead of
  // mounting an iframe.
  external?: boolean;
}

export interface CompanyTag {
  emoji: string;
  name: string;
  color: string; // Background color for the tag
}

export interface SquadMember {
  role: string; // e.g. "UX Lead", "Product Manager", "Engineering"
  name?: string; // Optional - only show if notable
  isMe?: boolean; // Highlights this as your role
  isLead?: boolean; // Differentiates leads from contributors (white vs muted role label)
}

export interface Squad {
  members: SquadMember[];
}

// Import and re-export COMPANIES for backwards compatibility
import { COMPANIES } from "./companies";
export { COMPANIES };

export interface Project {
  id: string;
  title: string;
  subtitle?: string;
  company: string;
  companyLogo?: string; // Path to company logo (square or horizontal lockup)
  companyUrl?: string; // Link to company website
  timeline: string;
  isProtected: boolean;
  // Protected /exp experiment slugs this case study embeds (e.g. ["n4", "n5"]).
  // Unlocking the case study also mints these experiments' session cookies so
  // the embedded prototypes render without a second password prompt.
  linkedExperiments?: string[];
  showLockIcon?: boolean; // Show lock icon without requiring password (for "coming soon" projects)
  passwordHint?: string; // Hint for protected projects (themed riddle)
  prodReady?: boolean; // For protected projects: show in prod? (default true)
  order?: number; // Display order (lower = first)
  ctaLabel?: string; // Optional text label for action button (e.g., "Now building")
  ctaIcon?: string; // Path to icon orb image for action button (shows in default state)
  brandColor?: string; // Hex color for project theming (defaults to #FF5E00)
  brandColorEnd?: string; // Gradient end color (bright end)
  companies?: CompanyTag[]; // Branded company tags (emoji + colored background)
  tags?: string[]; // Skill/category tags (outline style)
  squad?: Squad; // Team composition with your role highlighted
  metrics?: ProjectMetric[];
  heroDescription?: string; // Required for public projects, loaded via API for protected
  heroImage?: string;
  heroVideo?: string; // Video takes precedence over heroImage if both present
  heroVideoPlaybackRate?: number; // Default 1, set to 2 for sped-up videos
  heroCaption?: string;
  keyImpact?: string; // Required for public projects, loaded via API for protected
  keyImpactImage?: string;
  keyImpactCaption?: string;
  challenge?: string; // Required for public projects, loaded via API for protected
  challengeImage?: string;
  challengeImages?: { src: string; caption?: string }[]; // Multiple challenge images (grid layout)
  challengeImageCaption?: string;
  challengeImageAfterParagraph?: number; // 0 = after 1st paragraph (default), 1 = after 2nd, etc.
  solution?: string; // Required for public projects, loaded via API for protected
  liveDemos?: LiveDemo[]; // Live embedded prototypes (prototype-primary case studies)
  solutionMedia?: SolutionMedia;
  demoMedia?: SolutionMedia; // Demo image/video shown after results
  process?: ProcessStep[];
  processApproach?: ProcessApproach[]; // Alternative: pillar-based approach (2-col layout)
  whatShipped?: {
    caption?: string;
    platforms: {
      label: string;
      media?: { src: string; type: "image" | "video" };
      placeholder?: boolean; // If true, show gray box with label
      aspectRatio: string; // e.g. "16/9", "9/19.5"
    }[];
  };
  userQuotes?: UserQuote[];
  userQuotesHeader?: string; // Custom header for userQuotes section (default: "From fans")
  leadershipQuotes?: UserQuote[]; // External leadership quotes (CPO, press, etc.)
  teamQuotes?: UserQuote[]; // Internal team recognition/feedback
  pressCredentials?: { text: string; year?: string }[]; // Press mentions/credentials (plain text, not quotes)
  results?: ProjectResult[];
  resultsCaption?: string; // Caption shown below results grid (e.g. timeframe context)
  ratingsComparison?: ProjectResult[]; // Optional ratings comparison grid
  ratingsComparisonCaption?: string; // Caption shown below ratings comparison
  recognitionImages?: { src: string; caption?: string }[]; // Challenge coin, award, etc. (2-col if 2 items)
  conclusionImages?: { src: string; caption?: string }[]; // Images shown before recognition (double column if 2)
  conclusionImageLayout?: "single" | "grid"; // Force single column layout for conclusion images
  learnings?: string[];
  conclusion?: string; // Personal closing paragraph
}

export const projects: Project[] = [
  {
    id: "avp",
    order: 1,
    title: "Apple Vision Pro",
    subtitle: "A new Apple platform and 1.5 months to ship an anime app",
    company: "Crunchyroll",
    companyLogo: "/images/logo-crunchyroll.png",
    companyUrl: "https://www.crunchyroll.com",
    timeline: "Dec 2023–Feb 2024",
    isProtected: false,
    ctaIcon: "/images/icon/AVP-Icon.webp",
    brandColor: "#FF5E00",
    brandColorEnd: "#FFB300",
    companies: [COMPANIES.crunchyroll, COMPANIES.apple],
    tags: ["Spatial Design", "UX/UI", "Native App", "visionOS"],
    squad: {
      members: [
        // Leads
        { role: "Tech Lead", name: "Nick Kononezko", isLead: true },
        { role: "Product Lead", name: "Patrick Perez", isLead: true },
        { role: "UX Lead", name: "Vincent Ngu", isMe: true, isLead: true },
        // Contributors
        { role: "Engineer", name: "Allen Huang" },
        { role: "Engineer", name: "Cristian Lupu" },
        { role: "Engineer", name: "Ion Ostafi" },
        { role: "Engineer", name: "Mihai Seremet" },
      ],
    },
    metrics: [
      { value: "Day 1", label: "Launch" },
      { value: "4.8★", label: "Rating" },
      { value: "5.9x", label: "Reviews" },
      { value: "Featured", label: "App Store" },
    ],
    heroDescription:
      "As the sole designer, I shipped Crunchyroll’s Vision Pro app for Day 1 launch. Flew out to the war room, iterated live on hardware with the stealth squad. Shipped the highest-rated streaming app on visionOS, featured in the App Store alongside Disney+ and Max.",
    heroVideo: "/images/avp/avp-demo.mp4",
    heroCaption: "A demo of Crunchyroll’s visionOS app",
    keyImpact:
      "1.5 months, hardware to App Store. Strengthened Crunchyroll’s innovation partnership with Apple as a Day 1 launch partner. Featured in the visionOS _‘Watch It on the Big Screen’_ collection alongside Disney+ and Max.",
    challenge:
      "The team transplanted the iPadOS app before I joined. It failed immediately. Too dense for spatial viewing, too small for eye tracking. **My first meeting was Apple reviewing the port and rejecting it.**\n\nI looked at the build and agreed. Everything was way too dense for eye tracking. Mobile UI patterns made no sense in a spatial context. It was functional, barely. And ‘barely functional’ doesn’t fly on a $3,500 device where early adopters expect polish.\n\nI found out on a Thursday. My manager asked if I could be in LA by Monday. I worked through the night to get wireframes out. Not to ship, but **to prove we had a native vision worth keeping the dev kit for.**",
    solution:
      "We went fully native. Porting would’ve been faster but the audience wouldn’t accept it. People paying $3,500 after tax for bleeding-edge hardware are motivated early adopters. **They’ll tolerate imperfection, not lazy.** Crunchyroll couldn’t be ‘that app’ on a flagship Apple launch.\n\nEvery surface got rethought for spatial: glass UI, eye tracking, dwell interactions, depth and distance. And because the project was under strict NDA, I couldn’t get critique from my own design team. So I pushed for a design review with Apple’s visionOS specialists instead. If I can’t go to my team, I’ll go to the people who built the platform.",
    process: [
      {
        title: "Building Intuition",
        content:
          "No established patterns. No internal critique available. **I had to build spatial intuition for a medium I’d never touched.**\n\nBefore flying to LA, I spent the weekend on my Valve Index. Not a perfect comparison: tethered, controller-based, totally different interaction model. But it gave me a baseline for how content feels in a headset, how depth changes your read on density, and what breaks when you take flat UI into three dimensions. I studied Apple’s HIG docs on spatial computing, sourced early Vision Pro demos, and pulled from my years designing for living room platforms. visionOS sits in Crunchyroll’s living room suite, that’s why I was on the project. But spatial computing was new territory even within that world.\n\nI had to build the closest approximation I could. The hardware would come later.",
        images: [
          { src: "/images/avp/steam-vr.webp", caption: "Steam VR home: the closest spatial UI reference available before touching Vision Pro hardware" },
          { src: "/images/avp/process-research.mp4", caption: "Apple HIG documentation on spatial computing", type: "video" },
        ],
      },
      {
        title: "Scoping the Possible",
        content:
          "We couldn’t do everything in six weeks. The iPadOS port had already given us something useful: instead of guessing what spatial design should feel like, we had a concrete list of what it shouldn’t. Reduce density. Size for eye tracking, not touch. Let the glass UI breathe. Stop fighting the platform.\n\nI mapped every UX domain and made the priority framework. Product and engineering helped lock feasibility. Custom anime environments would’ve been the dream (imagine watching Attack on Titan inside Wall Maria) but we made the hard call to cut them. Polish over novelty. We focused eng resources on rock-solid playback and core browsing. The features we shipped had to be flawless. **_No room for half-baked on a $3,500 device._**",
        images: [
          { src: "/images/avp/process-poc-1.jpg", caption: "The port: what spatial design shouldn’t be" },
          { src: "/images/avp/scope-mvp.jpg", caption: "MVP scope" },
        ],
      },
      {
        title: "The Sprint",
        content:
          "Flew to LA to join the stealth squad in the war room. **First time touching the hardware in person.** I banked empirical data on spatial behavior: viewing distance, gaze decay, eye-tracking target sizes. Before flying home to Boston, I set up a local git environment so I could pull the latest builds and keep testing without flying back. Three surfaces came out of that sprint.\n\nThe project was under strict NDA, which meant no one outside the stealth squad could know it existed. **I couldn’t loop in fellow designers for critique even if I wanted to.** So I pushed for a design review with Apple’s visionOS team. Cross-functional call, our engineering present too. I came with specific questions about corner radius calculations for nested glass containers, legibility at distance, and how dwell, pinch, and hold interactions should actually behave.\n\nThe feedback reshaped key decisions. I’d been pulling color from hero art to create tinted glass backgrounds, sort of a stained-glass effect. Apple’s designer told me to stop fighting it and let the glass blend with the environment naturally. **Legibility improved the moment we made that change.** She also gave me a formula for calculating outer corner radii on nested glass windows, spatial-specific craft that wasn’t in any public docs at the time.\n\n**You can’t design for a medium you’ve never felt.**",
        imageGroups: [
          {
            images: [
              { src: "/images/avp/process-poc-2.jpg", caption: "Before: port density" },
              { src: "/images/avp/density-comparison.jpg", caption: "After: sized for eye tracking, not touch" },
            ],
          },
          {
            images: [
              { src: "/images/avp/wires-03.jpg", caption: "Home Feed" },
              { src: "/images/avp/wires-02.jpg", caption: "Series Page" },
            ],
          },
          {
            images: [
              { src: "/images/avp/wires-01.jpg", caption: "Video Player" },
            ],
          },
        ],
      },
    ],
    userQuotes: [
      {
        text: "Watching _Crunchyroll_ on my Vision Pro while doing interval training on my Carol bike: I think this is the specific, exact killer use-case for the AVP...",
        author: "@aaronwhite • Twitter",
      },
      {
        text: "The Apple Vision Pro is such godlike tech! _CRUNCHYROLL_ HAS A NATIVE APP ON APPLE VISION! WHY ARE WE NOT TALKING ABOUT THIS?!",
        author: "@JetSetHan • Twitter",
      },
      {
        text: "I’ve had a great experience with video playback in native visionOS apps (namely Apple TV+, but also _Crunchyroll_) and in the browser (Netflix)",
        author: "@NimaZeighami • Twitter",
        context: "↳ in reply to ported app quality",
      },
    ],
    results: [
      { value: "Day 1", label: "Launch Partner" },
      { value: "Featured", label: "App Store" },
      { value: "5.9x", label: "More Reviews" },
    ],
    ratingsComparison: [
      { value: "4.8★", label: "Crunchyroll" },
      { value: "4.1★", label: "Disney+" },
      { value: "3.3★", label: "Max" },
      { value: "2.8★", label: "Peacock" },
    ],
    ratingsComparisonCaption: "Measured within 1 month of launch",
    recognitionImages: [
      { src: "/images/avp/hero.png", caption: "Crunchyroll’s Hime-chan celebrating launch day 🧡" },
      { src: "/images/avp/challenge-coin.jpg", caption: "Earned my first challenge coin for accomplishing Project Gojo and delivering at a high bar of excellence" },
    ],
    conclusionImages: [
      { src: "/images/avp/solution.jpg", caption: "Crunchyroll’s App Store listing on visionOS" },
      { src: "/images/avp/app-store-featured.png", caption: "Featured in ‘Watch It on the Big Screen’ collection" },
    ],
    conclusionImageLayout: "single",
    learnings: [
      "When you’re brought in late, your first job is buying time. Those overnight wireframes kept the dev kit.",
      "Can’t access your own team? Go to the source. Apple’s visionOS designers became my critique partners.",
      "Spatial computing breaks every assumption you have about density, hierarchy, and sizing. Trust your instincts, but recalibrate everything.",
    ],
    conclusion: "Seeing fans discover anime in spatial computing for the first time made every late night worth it.",
  },
  {
    id: "esr",
    order: 3,
    title: "End Slate Recommendations",
    subtitle: "A blank screen right when fans wanted more",
    company: "Crunchyroll",
    companyLogo: "/images/logo-crunchyroll.png",
    timeline: "May 2024–March 2025",
    isProtected: false,
    ctaIcon: "/images/icon/ESR-Icon.webp",
    brandColor: "#FF5E00",
    brandColorEnd: "#FFB300",
    companies: [COMPANIES.crunchyroll],
    tags: ["Feature Design", "UX/UI", "Cross-Platform", "Living Room", "Mobile", "Web"],
    squad: {
      members: [
        { role: "TPM Lead", name: "Alina Metreniuc", isLead: true },
        { role: "Tech Lead", name: "Raju Kovvuru", isLead: true },
        { role: "Product Lead", name: "Sophie McNutt", isLead: true },
        { role: "UX Lead", name: "Vincent Ngu", isMe: true, isLead: true },
        { role: "Motion Design", name: "Brian Lee" },
        { role: "Web Design", name: "Gregory Gallagher" },
        { role: "UX Research", name: "Hana Carpenter" },
        { role: "Mobile Design", name: "Lily Rokhva" },
        { role: "Content Design", name: "Mohammed Raza" },
        { role: "Visual Design", name: "Samantha Co" },
      ],
    },
    metrics: [
      { value: "692M", label: "Minutes Watched" },
      { value: "34M", label: "Video Plays" },
      { value: "10M", label: "Users" },
    ],
    heroVideo: "/images/esr/esr-hero-web.mp4",
    heroVideoPlaybackRate: 2,
    heroCaption: "Kira pagination on web: full-bleed hero art with sparkle indicators for browsing recommendations",
    heroDescription:
      "Users were cancelling because shows ended and we gave them nothing. After the last episode, a blank screen. As sole design lead coordinating 6 designers across motion, web, mobile, visual, content, and research, I designed the system that fills it.",
    keyImpact:
      "590K+ users per month discovering new content through ESR. eight platforms spanning TVs, consoles, streaming devices, mobile, and web. Closed a critical competitive gap with Netflix, Disney+, and Hulu. The Kira pagination pattern is now spreading to other carousels platform-wide.",
    challenge:
      "A leading reason users cancelled Crunchyroll: ‘The show I was watching ended.’\n\nAnime fans subscribe when new seasons release, watch their show, then cancel. Data showed a retention cliff: users who watched fewer series churned at significantly higher rates. **After the last episode, users saw nothing.** A blank screen at the exact moment they’re deciding ‘what next?’ while still emotionally connected.\n\nThe existing recommendation system wasn’t helping either. Users were literally telling each other to use MyAnimeList instead. Netflix, Disney+, and Hulu all had end slate recommendations. ==Crunchyroll had a black void.==\n\nMy challenge: lead design across 8+ living room platforms (tvOS, Roku, PlayStation, Xbox, Android TV, Fire TV, smart TVs), each with unique input patterns, certification requirements, and performance constraints, while coordinating my design team.",
    solution:
      "Kira (キラ, ‘sparkle’) pagination: **one recommendation at a time, infinite depth.**\n\nFull-bleed hero art catches fans at the moment they’re most engaged. Vertical sparkle indicators let you browse through recommendations without breaking immersion. The form factor of a single focused recommendation with the utility of a full carousel.\n\nThe pattern adapts to each platform’s interaction model: vertical sparkle stack for d-pad navigation on TV, horizontal dots with swipe gestures on mobile, click/keyboard on web. Same content, adapted UI.\n\nThe research insight was that users consistently wanted multiple recommendations. That didn’t mean cramming cards onto the screen. Kira delivers that utility in a different form: browse through recs one at a time, each with contextual reasoning explaining why it’s being recommended.",
    process: [
      {
        title: "Research & Discovery",
        content:
          "Fifty-four percent of users look for new anime directly after a finale. That’s when they’re most engaged. And that’s when we were showing them a blank screen. I partnered with the recommendations team on a 77-user survey and ran an 8-user prototype study on living room to map the full picture. The gap was worse than we suspected.",
        stats: [
          { number: 54, label: "Search After Finale" },
          { number: 74, label: "Want Similar to Favorite" },
          { number: 83, label: "Want Mood Match" },
          { number: 89, label: "Want Social Validation" },
        ],
      },
      {
        title: "Exploration",
        content:
          "Three prototypes, eight users. Solo Focus: one recommendation, cinematic and frictionless. Limited Focus: three modular slots balancing familiar and new. Scalable Focus: horizontal carousel, maximum density. Users were clear about what they wanted: more options. Solo felt like a gamble. One shot, no fallback.",
        contentAfter:
          "But the carousel created a different problem. It felt like the browse experience they were already trying to escape. **More options, yes. But not like that.**",
        images: [
          { src: "/images/esr/uxr-esr-a.mp4", type: "video", caption: "Solo Focus: 1 recommendation, cinematic and frictionless", controls: true },
          { src: "/images/esr/uxr-esr-b.mp4", type: "video", caption: "Limited Focus: 3 modular slots, balance of wants and discovery", controls: true },
          { src: "/images/esr/uxr-esr-c.mp4", type: "video", caption: "Scalable Focus: horizontal carousel, maximum density", controls: true },
        ],
      },
      {
        title: "Introspection",
        content:
          "Hit a wall exploring forward. Every direction felt like a compromise. Either too sparse or too dense. So I looked backward. Revisited the Solo prototype. The form factor was right: cinematic, immersive, respects the emotional moment of finishing a series. The problem was depth. One recommendation felt like a gamble.\n\nThe breakthrough: Solo with pagination. Same full-bleed hero treatment. Same cinematic simplicity. But you can browse through 4-6 recommendations without the visual overwhelm of a carousel. The sparkle indicators give a sense of depth without cluttering the screen.\n\nThat’s Kira (キラ). **The answer was behind me the whole time.**",
      },
      {
        title: "The Sparkle",
        content:
          "This project became a bright spot for a design team that needed wins. In a year of organizational change and competing priorities, ESR crits became something people actually looked forward to. Parallel tracks with Sam, Lily, Brian meant constant refinement even when I was in stakeholder meetings.",
        images: [
          { src: "/images/esr/expl-bubble-pagination.mp4", type: "video", caption: "Bubble pagination", aspectRatio: "3.65/1" },
          { src: "/images/esr/expl-star-splash-horz.mp4", type: "video", caption: "Star splash", aspectRatio: "3.65/1" },
        ],
        contentAfter:
          "The sparkle glyph went through its own journey. The team explored dozens of pagination concepts before landing on the star shape. **That was the breakthrough, and it set off another round of iterations.** Chunky stars, thin stars, geometric stars. We refined rotation matrices and timing curves until the motion felt organic, evolving from flat forms into the final Kira constellation.",
        imageGroups: [
          {
            images: [
              { src: "/images/esr/expl-svelte-shine-comp.mp4", type: "video", caption: "Shine effect" },
              { src: "/images/esr/expl-glyphs-new-star.mp4", type: "video", caption: "Final Kira shape" },
            ],
          },
        ],
      },
      {
        title: "The Constraints",
        content:
          "Legal came in hot. The original plan was to trigger End Slate at the start of end credits. But anime is different from Western TV: credits often contain canon content. Post-credits scenes. Story beats. Legal’s response: ‘We need to respect the credits.’\n\nI mapped out multiple implementation options ranging from cinematic to conservative, weighing visual impact against cross-platform consistency.\n\nThe call: ship a player UI trigger for V1 that could reliably work across all eight platforms. The autoskip data came back unreliable. Users were complaining about being skipped past content they wanted to see. We rolled it back. And the recommendations team went through org churn mid-project, reducing the recommendation sources available at launch.\n\n**None of this is complaining. It’s explaining the physics.**",
      },
      {
        title: "What Shipped",
        content:
          "Kira pagination shipped across all platforms. TVs, consoles, streaming devices, phones, tablets, web. Same pattern, adapted for each device’s interaction model. After months of constraints, scope cuts, and org churn, **seeing it live on eight platforms felt like proof that the foundation held.**",
        imageLayout: "single",
        images: [
          { src: "/images/esr/esr-hero-web.mp4", type: "video", caption: "Responsive Web", playbackRate: 2 },
          { src: "/images/esr/expl-esr-mobile-android-trimmed.mp4", type: "video", caption: "Phones & Tablets" },
          { src: "/images/esr/esr-lrx.mp4", type: "video", caption: "Living Room" },
        ],
      },
    ],
    results: [
      { value: "692M", label: "Minutes Watched" },
      { value: "34M", label: "Video Plays" },
      { value: "10M", label: "Users Engaged" },
    ],
    resultsCaption:
      "Measured within 7 days of seeing recommendations. Hundreds of thousands of users discovering new content monthly through ESR.",
    userQuotes: [
      {
        text: "I found myself feeling empty when the final episode ended of that anime, and I wanted to find something that filled that void.",
        context: "On the emotional moment",
      },
      {
        text: "I want to see why something is being recommended for me. Tags like ‘others who enjoyed this series also enjoyed...’ hold a lot of weight.",
        context: "On social proof",
      },
      {
        text: "3 on a single page is neat, but I’d appreciate having a way to see more.",
        context: "The insight that became Kira",
      },
    ],
    teamQuotes: [
      {
        text: "_@Vincent Ngu_, you did an excellent job pushing the project forward. I relied on you a lot to catch me up on the dozens of facets this project has.",
        author: "Brian Lee • ESR Retrospective",
      },
      {
        text: "Special ‘Friend of Content’ Award goes to _@Vincent Ngu_ for creating the right opportunities and setting the right conditions for content to shine.",
        author: "Mohammed Raza • ESR Retrospective",
      },
    ],
    learnings: [
      "Research reveals the need, not the solution. Users consistently wanted multiple recommendations. Kira delivered that utility in a form factor nobody described.",
      "The answer might be behind you. After endless forward exploration, the breakthrough came from revisiting a discarded prototype with fresh eyes.",
      "Design leadership is setting conditions. Parallel tracks, clear ownership, crits that energize instead of drain. The feature ships because the team ships.",
      "Constraints sharpen the case for V2. The numbers validate the foundation, and improvements are in progress.",
    ],
    conclusion:
      "V2 is in progress. Kira is becoming a system.\n\nWild what started as **‘why is there a blank screen here?’**",
  },
  {
    id: "cmp",
    order: 2,
    title: "Crunchyroll Profiles",
    subtitle: "A global streaming service with no profiles system",
    company: "Crunchyroll",
    companyLogo: "/images/logo-crunchyroll.png",
    companyUrl: "https://www.crunchyroll.com",
    timeline: "Q4 2023–April 2024",
    isProtected: false,
    ctaIcon: "/images/icon/MP-Icon.webp",
    brandColor: "#FF5E00",
    brandColorEnd: "#FFB300",
    companies: [COMPANIES.crunchyroll],
    tags: ["Feature Design", "UX/UI", "Cross-Platform", "Living Room"],
    squad: {
      members: [
        // Leads
        { role: "Web Design Lead", name: "Florence Yuen", isLead: true },
        { role: "Mobile Design Lead", name: "Keeley Laures", isLead: true },
        { role: "LRX Design Lead", name: "Vincent Ngu", isMe: true, isLead: true },
        // Contributors
        { role: "Motion Design", name: "Brian Lee" },
        { role: "Content Design", name: "Mohammed Raza" },
      ],
    },
    metrics: [
      { value: "4M+", label: "Profiles" },
      { value: "95.8%", label: "Success Rate" },
      { value: "80%", label: "Active Usage" },
      { value: "73%", label: "Above Goal" },
    ],
    heroDescription:
      "As LRX Domain Lead, I owned the living room experience for Crunchyroll’s most-requested feature. Designed profile management across TVs, consoles, and streaming devices, closing the gap with Netflix and Disney+ while giving the feature its personality through details like the Yuzu animation.",
    heroVideo: "/images/mp/yuzu-demo.mp4",
    heroCaption: "One of our most requested features, finally available to fans. [**_Crunchyroll News_**](https://www.crunchyroll.com/news/announcements/2024/4/3/mutiple-profiles-crunchyroll)",
    keyImpact:
      "8 platforms, one cohesive experience. 4M+ profiles created. Enabled legitimate household sharing at scale, driving premium upsells through profile tier limits.",
    challenge:
      "Crunchyroll operated for 11 years as a global platform without profiles. For five years, fans posted about it constantly: shuffled watch history, spoiled recommendations, lost episode progress. One post summed it up: ‘If Crunchyroll had profiles I would pay for a membership.’ This wasn’t just a feature request. ==It was a broken promise.==\n\nWe needed profiles to compete with Netflix and Disney+, reduce password sharing, and convert households into paying subscribers. My challenge: deliver a consistent experience across TVs, consoles, and streaming devices, each with unique constraints and interaction patterns, while staying in sync with my pod covering Web and Mobile.",
    challengeImage: "/images/mp/reddit.jpg",
    challengeImageCaption: "Fan discussions spanning five years across r/Crunchyroll",
    solutionMedia: { src: "/images/mp/hero.jpg", type: "image", caption: "Profile loading screen with personality" },
    solution:
      "Profiles needed to launch simultaneously across every platform. Roku got hit with a reorg. Two month delay. But profiles can’t roll out in patches: **your phone has profiles, but your TV doesn’t? That’s a broken experience.** So I co-scoped ==‘Roku Lean Profiles’==: minimum viable profile experience for Roku to unblock launch everywhere else. **Shipped to fans everywhere instead of nowhere.**\n\nI was part of a senior design pod: Florence on Web, Keeley on Mobile, me on Living Room. On the fan-facing side, I moved age restrictions from account-level to profile-level so families could actually share, and adapted the switching UI across platforms: d-pad navigation on TV remotes, game controller inputs on consoles, pointer interaction on smart TVs. The goal wasn’t identical UI across every device. It was finding the lowest common denominator that still respected each platform’s strengths and certification requirements. Behind the scenes, I caught avatar and background asset quality issues pre-launch, including cases where character faces would be obscured and IP treatments that could’ve caused licensor friction. I designed graceful failure states and reusable error handling patterns that kept the experience consistent despite platform fragmentation.\n\nI had a vision for the Yuzu profile selection animation, and our motion designer brought it to life as his first big project. The **_‘one sec, increasing power levels’_** loading voice just came to me one day and stuck. Yuzu hanging off the avatar. These gave the feature a personality that fans noticed.",
    results: [
      { value: "4M+", label: "Profiles Created" },
      { value: "95.8%", label: "Creation Success" },
      { value: "80%", label: "Active Usage" },
      { value: "73%", label: "Above Adoption Goal" },
    ],
    resultsCaption: "Launch drove premium upsells through profile tier limits, reduced password sharing through legitimate household access, and achieved competitive parity with Netflix and Disney+.",
    learnings: [
      "Cross-platform consistency means equivalent quality, not identical UI",
      "Domain ownership enables depth. Regular syncs maintain coherence",
      "When one platform is behind, define the minimum to unblock others and fast-follow",
    ],
    userQuotes: [
      {
        text: "It’s definitely not working with my two kids and I sharing a single account. The kids are way ahead of me and I don’t remember which episode I’m on. This is a welcome change.",
        author: "Anteros • ResetEra",
        context: "On household sharing",
      },
      {
        text: "Their app update that showed your most recently watched stuff got real annoying if you’re sharing an account. Plus I had to keep track of what episode I’m on if someone else watches the same show.",
        author: "Mortemis • ResetEra",
        context: "On the pain point",
      },
      {
        text: "My sister lets me use hers but I never use it because of the profile situation. Genuinely great news.",
        author: "Cipher Peon • ResetEra",
        context: "On the wait",
      },
      {
        text: "#crunchyroll finally getting multiple profiles 😩✨💃 I AM EXCITED. ARE YOU?!",
        author: "@ngovoconvo • TikTok",
        context: "On the announcement",
      },
    ],
    leadershipQuotes: [
      {
        text: "Finally. We have been listening to viewer requests and our team has been hard at work to roll out the functionality for multiple profiles on _Crunchyroll_.",
        author: "Kaliel Roberts, CPO",
        url: "https://www.polygon.com/24120007/crunchyroll-profiles-streaming-anime",
      },
    ],
    conclusion:
      "Profiles was the most-requested feature for five years. **Shipping it felt like finally keeping a promise.**",
  },
  {
    id: "panera-mobile",
    order: 4,
    title: "Panera Rapid Reorder",
    subtitle: "A 9-tap flow that competitors solved in 3",
    company: "Panera Bread",
    companyLogo: "/images/logo-panera.png",
    companyUrl: "https://www.panerabread.com",
    timeline: "Dec 2018–Jan 2019",
    isProtected: false,
    ctaIcon: "/images/icon/RR-Icon.webp",
    brandColor: "#3A9656",
    brandColorEnd: "#00FF55",
    companies: [COMPANIES.panera],
    tags: ["Feature Design", "UX/UI", "Native App", "Mobile"],
    squad: {
      members: [
        { role: "Product Lead", name: "Becca Shaw", isLead: true },
        { role: "Tech Lead", name: "Matthew McGann", isLead: true },
        { role: "UX Lead", name: "Vincent Ngu", isMe: true, isLead: true },
      ],
    },
    metrics: [
      { value: "$3M+", label: "Revenue" },
      { value: "260K+", label: "Orders" },
      { value: "4.82★", label: "Satisfaction" },
      { value: "Featured", label: "App Store" },
    ],
    heroDescription:
      "Panera’s reorder flow took 9 taps. Chipotle’s took 3. As UX Lead, I grabbed my prototypes and headed to Boston-area bakery-cafes to test with real customers. Validated three approaches, shipped the simplest one. Four taps: open, select, confirm, done.",
    heroVideo: "/images/rr/hero.mp4",
    heroCaption: "Before (9 taps) vs after (4 taps) reorder flow",
    keyImpact:
      "**2,166 hours of customer time saved.** 30 seconds per order × 260K+ orders in the first quarter. Highest satisfaction score (4.82★) among all Panera ordering methods.",
    challenge:
      "Panera’s ‘Better Breakfast on the Go’ initiative hit a wall: ==reordering took 9 taps vs Chipotle’s 3.== Friction affecting thousands of daily orders, especially our most loyal customers who ordered the same items repeatedly. Data showed ninety percent of mobile orders were ASAP pickup at the same location, yet **we forced regulars through unnecessary steps** like location selection despite average users visiting fewer than 2 bakery-cafes.",
    solution:
      "Designed a personalized reorder system surfacing past orders by time-of-day, auto-remembering location and customizations. **Four taps: open ▸ select ▸ confirm ▸ done.** Reduced ordering time from forty seconds to ten seconds, a 75 percent improvement.\n\nThe key insight: our users split into “Tried & True” (5-8 favorites) and “Explorers” (9-12 items). **Pulldown served both by itemizing recent orders**, letting regulars grab their usuals while Explorers could mix in something new. Validated with field research before full build, then phased rollout to prove business case.",
    process: [
      {
        title: "Research & Discovery",
        content:
          "Competitive analysis revealed Panera required 9 taps to reorder vs Chipotle’s 3. Chipotle could do it in 3 because they sell bowls and wraps. Panera has a much bigger menu with more customization options. **We couldn’t just copy their flow.**\n\nUser research uncovered two distinct segments: ‘Tried & True’ customers (5-8 favorite items, highly habitual) and ‘Explorers’ (9-12 items, always trying new things). Any solution would need to serve both without forcing one behavior over the other.",
        image: {
          src: "/images/rr/competitive-taps.mp4",
          caption: "Panera required 3x more taps to reorder than competitors",
          type: "video",
        },
        layout: "side-by-side",
      },
      {
        title: "Exploration & Ideation",
        content:
          "After brainstorming sessions with my squad and stakeholders, I developed three distinct prototypes: ‘Pulldown’ (quick access list from home), ‘Home Page’ (order history in carousel), and ‘Speed Dial’ (two-tap with undo timer). Each offered different advantages, and I needed real user feedback to determine the optimal solution.",
        images: [
          {
            src: "/images/rr/prototype-pulldown.mp4",
            caption: "‘Pulldown’ - Quick access list from home screen",
            type: "video",
          },
          {
            src: "/images/rr/prototype-homepage.mp4",
            caption: "‘Home Page’ - Order history in carousel",
            type: "video",
          },
          {
            src: "/images/rr/prototype-speeddial.mp4",
            caption: "‘Speed Dial’ - Two-tap with undo timer",
            type: "video",
          },
        ],
      },
      {
        title: "Field Testing & Validation",
        content:
          "Grabbed my prototypes and headed to two Boston-area bakery-cafes with stakeholders in tow. 14 customer interviews gave us new qualitative signal to weigh against our quantitative data.\n\n**No clear winner emerged from testing alone.** Speed Dial scored well for speed, but users said it felt ‘too fast.’ I iterated with an undo button, but engineering flagged a risk: if users closed the app before the timer finished, orders could go unplaced. Home Page tested well but had asset maintenance costs that wouldn’t scale. Pulldown scored consistently across metrics without the technical risks.\n\nThe decision came down to tradeoffs: usability scores, engineering feasibility, long-term maintainability. We deliberated as a squad and landed on Pulldown.",
        image: {
          src: "/images/rr/field-day-results.png",
          caption: "Field testing results across 14 customers at 2 bakery-cafes",
        },
        images: [
          {
            src: "/images/rr/learnings-pulldown.png",
            caption: "Pulldown: scored well, most flexible to build",
          },
          {
            src: "/images/rr/learnings-homepage.png",
            caption: "Home Page: asset maintenance issues",
          },
          {
            src: "/images/rr/learnings-speeddial.png",
            caption: "Speed Dial: users felt it was ‘too fast’",
          },
        ],
      },
      {
        title: "Design Development",
        content:
          "Refined the Pulldown approach with icon exploration. Lightning bolt beat clock/stopwatch icons which cued ‘history’ more than ‘speed’. Before full development, I pushed for a phase 0 test: add the Reorder button but direct to existing Past Orders. **This low-lift test validated the business case before committing to the full build.**",
        images: [
          {
            src: "/images/rr/icon-exploration.jpg",
            caption: "Icon exploration: lightning bolt beat clocks/stopwatches",
          },
          {
            src: "/images/rr/button-bar.png",
            caption: "Final CTA lockup with the new Reorder button",
          },
        ],
      },
      {
        title: "High-Fidelity & Handoff",
        content:
          "Produced detailed wireframes breaking implementation into phases. Worked closely with developers throughout handoff: detailed specs, implementation reviews, visual consistency between iOS and Android. The phased approach: Phase 0 (quick access to Past Orders), Phase 1 (Rapid Pick-Up with sorting), Phase 2 (Delivery + time-based sorting).",
        image: {
          src: "/images/rr/showcase.mp4",
          caption: "High-fidelity prototype of the Rapid Reorder flow",
          type: "video",
        },
        images: [
          {
            src: "/images/rr/mockups-1.jpg",
            caption: "Splash ▸ Home ▸ Announcement",
          },
          {
            src: "/images/rr/mockups-2.jpg",
            caption: "Rapid Reorder UI ▸ Location handling",
          },
          {
            src: "/images/rr/mockups-3.jpg",
            caption: "Location update ▸ Tap interaction ▸ Added state",
          },
          {
            src: "/images/rr/mockups-4.jpg",
            caption: "Checkout ▸ Processing ▸ Confirmation",
          },
        ],
      },
    ],
    results: [
      { value: "260K+", label: "Orders (~$3M)" },
      { value: "55%", label: "iOS Conversion" },
      { value: "4.82★", label: "Satisfaction" },
      { value: "Featured", label: "App Store" },
    ],
    resultsCaption: "Measured within first quarter of launch",
    conclusionImages: [
      {
        src: "/images/rr/app-store-featured.jpg",
        caption: "Featured on Apple’s App Store as Editor’s Choice with 4.8★ rating",
      },
    ],
    userQuotes: [
      {
        text: "Reorder button is magic. Great work!",
        context: "5★ On the reorder button",
      },
      {
        text: "Oh my god I love this reorder tool!! Finally!!",
        context: "5★ On the wait",
      },
      {
        text: "The new shortcuts for reordering are amazing!!!!! So fast and so simple!",
        context: "5★ On speed",
      },
    ],
    userQuotesHeader: "From fans • First 2 weeks",
    pressCredentials: [
      {
        text: "Featured in [CNN Business and Restaurant Dive](https://www.cnn.com/2019/04/15/business/panera-breakfast-strategy) as a key component of Panera’s national digital ordering strategy.",
        year: "2019",
      },
    ],
    learnings: [
      "Competitive benchmarking creates urgency. That three-tap difference motivated the whole team to move fast",
      "Prototyping multiple approaches lets you validate with confidence. Testing three concepts revealed why the simplest one won",
      "Field research with real customers builds stakeholder confidence and reveals insights analytics can’t",
    ],
    conclusion:
      "**2,166 hours of customer time saved.** That’s what obsessing over a single, high-frequency interaction gets you. Full ownership from research to launch, and the highest satisfaction scores across all ordering methods.\n\n**The reorder insight didn’t stop at mobile.** Two years later, it became the foundation for Panera’s [**_Alexa voice ordering experience ▸_**](/panera-voice).",
  },
  {
    id: "panera-voice",
    order: 5,
    title: "Panera Voice Ordering",
    subtitle: "A conversational ordering experience for restaurants in 2017",
    company: "Panera Bread",
    companyLogo: "/images/logo-panera.png",
    companyUrl: "https://www.panerabread.com",
    timeline: "2017 (Google) • 2019 (Amazon)",
    isProtected: false,
    ctaIcon: "/images/icon/VUX-Icon.webp",
    brandColor: "#3A9656",
    brandColorEnd: "#00FF55",
    companies: [COMPANIES.panera, COMPANIES.google, COMPANIES.amazon],
    tags: ["Feature Design", "VUX/VUI", "Multi-Modal", "Voice"],
    squad: {
      members: [
        { role: "PM (Alexa)", name: "Chad Cohen", isLead: true },
        { role: "PM (Google)", name: "Jeremy Tripp", isLead: true },
        { role: "Tech Lead", name: "Shivany Shenoy", isLead: true },
        { role: "UX Lead", name: "Vincent Ngu", isMe: true, isLead: true },
      ],
    },
    metrics: [
      { value: "Google I/O", label: "2017 Keynote" },
      { value: "1st", label: "QSR Voice" },
      { value: "Amazon", label: "2019 Launch" },
      { value: "5★", label: "Alexa Reviews" },
    ],
    heroDescription:
      "2017: Google wants a launch partner for Assistant Transactions. No QSR had done voice ordering. Two months later, we’re on stage at I/O. 2019: Amazon calls. This time I know what matters. One primary flow, laser-focused execution. Featured in their product launch.",
    heroImage: "/images/vux/hero.webp",
    heroCaption: "Google Assistant ordering flow with upsell and checkout",
    keyImpact:
      "1st QSR to ship voice ordering. 2 platforms, 2 years apart, same lead designer. Showcased at Google’s I/O keynote (2017) and featured in Amazon’s 2019 product launch.",
    challenge:
      "Google’s Assistant Transactions was still in stealth. They invited a handful of brands to compete for a keynote spot at I/O 2017. ==No QSR had shipped voice ordering before.== **We had two months to design, build, and prove we deserved that stage.** Two years later, Amazon came calling: they’d seen the keynote, wanted Panera for Alexa’s food ordering launch. Same pitch, different platform.",
    solution:
      "For Google, we modeled the voice after real bakery-café associates and **built over ten intents covering the full ordering journey.** After a qualifying phase against other brands, we won the keynote slot. For Amazon two years later, the focus was crystal clear: reordering at your go-to Panera. Morning coffee on your commute, dinner on a weeknight, whatever your usual looks like. Post-launch data from Google confirmed what we’d already proven with [**_Rapid Reorder ▸_**](/panera-mobile): that’s what users actually wanted. We still supported things like rewards and account info for convenience, but every design decision served that primary path.",
    process: [
      {
        title: "Research & Discovery",
        content:
          "Voice design in 2017 was the Wild West. I picked up a VUI design book and mapped what made each assistant special. Google’s superpower was context: deep connection to users’ daily lives through their data ecosystem. Siri had tighter OS integration, Alexa was the shopping expert, but Google could check your calendar, notice you had a meeting across town, and suggest when to leave. That contextual awareness shaped our approach. For Panera’s voice personality, I listened to recordings of real associates taking orders. Their phrases, their warmth, their recovery from confusion became our north star.",
        image: { src: "/images/vux/va-landscape.png", caption: "Voice assistant landscape in 2017" },
      },
      {
        title: "The Spaghetti Diagram",
        content:
          "My initial flows were too rigid: perfect linear paths that collapsed when humans went off-script. After guerrilla testing where I played the assistant, I had my breakthrough: traditional UX flows don’t work for voice. I developed my ‘Spaghetti Diagram’, a messy web of conversation paths reflecting how people actually talk. The small talk intents were my favorite: “How old are you?” “Who’s your boss?” Personality in unexpected places.",
        images: [
          { src: "/images/vux/spaghetti-diagram.png", caption: "The ‘Spaghetti Diagram’: conversation paths reflecting natural speech" },
          { src: "/images/vux/dialog-flow-smalltalk.jpg", caption: "Dialogflow small talk intents added personality in unexpected places" },
        ],
      },
      {
        title: "Google I/O Keynote (2017)",
        content:
          "We ran an internal beta with HQ employees to stress-test edge cases before the qualifying demo. After an intense qualifying phase, Panera was selected as Google’s launch partner. Our Action was recognized as the most robust and polished among all candidates, earning us a showcase spot during the keynote.",
        image: { youtubeId: "fAjbPdoQjVk", type: "youtube", caption: "Panera featured at Google I/O 2017 keynote" },
      },
      {
        title: "The Pivot",
        content:
          "Post-launch data told the real story: reordering dominated usage. We built over ten intents. Users used one. The same insight we’d validated with [**_Rapid Reorder ▸_**](/panera-mobile) on mobile, now showing up in voice. Users just wanted to grab their usual. Fast.\n\nTwo years later, Amazon called. They’d seen the I/O keynote, wanted Panera for Alexa’s food ordering launch. The timing was perfect: we’d just shipped Rapid Reorder on mobile to massive success. Same insight, different platform. Amazon also wanted us for Echo Auto, their upcoming in-car device. Commuters reordering breakfast on their drive to work. The reordering focus now had real strategic teeth.",
      },
      {
        title: "Focused Execution",
        content:
          "The primary path was reordering. Your usual, at your go-to Panera. We still supported rewards and account info for convenience, but every design decision served that core flow. ‘Alexa, ask Panera to reorder my usual.’ The constraint was liberating. We created an internal Early Access Program where HQ employees placed real orders to the nearby Sunset Hills bakery-café. Real-world testing, real refinements.",
        image: { src: "/images/vux/brainstorm.mp4", caption: "Early sketches for the Alexa reorder flow", type: "video" },
        layout: "side-by-side",
      },
      {
        title: "Multi-Modal Design System",
        content:
          "Alexa’s diverse hardware was the design challenge: voice-only Echo Auto for commuters, Echo Spot’s tiny circular display, Fire TV’s full living room screen. Three completely different interaction models under one skill. I built an adaptive system that scaled across the full spectrum while maintaining brand consistency. Featured in Amazon’s 2019 product launch. Five-star reviews praising the simplicity.",
        images: [
          { src: "/images/vux/alexa-spot.mp4", caption: "Echo Spot", type: "video" },
          { src: "/images/vux/alexa-firetv-1.mp4", caption: "Fire TV: browsing", type: "video" },
          { src: "/images/vux/alexa-firetv-2.mp4", caption: "Fire TV: checkout", type: "video" },
        ],
      },
    ],
    leadershipQuotes: [
      {
        text: "The future is really about going from thought to order in a matter of seconds. We had this dream when we first started, many years ago, but the technology was just not available.",
        author: "Mark Yoshitake, GM of Alexa Skills",
        publication: "Retail Brew • 2023",
        context: "From Amazon",
      },
    ],
    userQuotes: [
      {
        text: "I love this skill for one of my favorite cafes! Works very well - a real time-saver for me :-)",
        author: "Amazon Customer",
        context: "5★ Works great, remembers past orders",
      },
      {
        text: "I signed in and it easily got my last few orders; and checked my rewards.",
        author: "Suraj",
        context: "5★ Great for simple reordering",
      },
      {
        text: "Had absolutely no problem adding the skill and linking my Panera account.",
        author: "Debra M.",
        context: "5★ Very Easy to Set Up",
      },
    ],
    results: [
      { value: "Google I/O", label: "2017 Keynote" },
      { value: "1st", label: "QSR Voice" },
      { value: "Amazon", label: "2019 Launch" },
      { value: "5★", label: "Alexa Reviews" },
    ],
    conclusionImages: [
      { src: "/images/vux/amazon-announcement.png", caption: "Featured in Amazon’s 2019 Alexa Skills announcement alongside Uber, Audible, and Headspace" },
    ],
    learnings: [
      "Traditional UX flows break in voice. Real conversation is a web, not a line",
      "Your best employees already embody your brand voice. Study them before inventing personas",
      "Comprehensive isn’t always better. Sometimes doing one thing flawlessly wins",
    ],
    conclusion:
      "The I/O keynote was the highlight. But the real lesson came two years later: knowing what to leave out matters as much as knowing what to include. **Five-star reviews for an app that does exactly one thing.**\n\nP.S. It’s now 2026, and the infrastructural evolution of AI has finally caught up to the vision product leaders had when they first rolled out voice assistants. **Agentic AI is here. Wild.**",
  },
  // IDS (In-Player Subtitle a11y) moved behind the password gate 2026-08-06:
  // shell in protected-projects.ts, narrative in protected-content.ts.
  // Prototype-primary reframe — the /exp/n4 prototype IS the showcase; the
  // PROJECT_CSC_PASSWORD unlock also mints its session cookie. (n5 stays a
  // separate future case study.) Slug is "csc" (V 2026-08-07): IDS was the
  // discovery phase; the initiative became Customizable Subtitles & Captions (CSC),
  // and the case study now carries the initiative's own name.
];

// In dev: include all protected projects for testing
// In prod: only public projects + prodReady protected projects
import { protectedProjects } from "./protected-projects";

const IS_DEV = process.env.NODE_ENV === "development";

// Public projects for client use
// Dev: all projects including protected
// Prod: public projects + protected projects with prodReady: true
// Sorted by order field (lower = first)
const allProjects = IS_DEV
  ? [...projects, ...protectedProjects]
  : [...projects, ...protectedProjects.filter(p => p.prodReady !== false)];

export const publicProjects = allProjects.sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
